import { useState } from 'react';
import { Button, Checkbox, CheckboxProps, Col, Form, Input, Row } from 'antd';
import { useNavigate } from 'react-router-dom';
import { FormProps } from 'antd/es/form/Form';

// services
import NotificationService from 'core/services/notification';
import { CreateCurrencyPayload, onCreateCurrency } from 'modules/currencies/services/currencies';

// components
import BackButton from 'components/backButton/backButton';
import { useTranslation } from 'react-i18next';

type FieldType = {
	'name[en]': string;
	'name[ar]': string;
	code: string;
	default: 1 | 0;
};

function CreateCurrencyPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [createForm] = Form.useForm();

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const createCurrencyPayload: CreateCurrencyPayload = {
				name: {
					ar: values['name[ar]'],
					en: values['name[en]'],
				},
				active: 1,
				default: values.default ? 1 : 0,
				code: values.code,
			};

			await onCreateCurrency(createCurrencyPayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('CURRENCIES.CREATED_SUCCESS'));
			navigate('/currencies');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.OPS'), t('CURRENCIES.CREATE_ERROR'));
		}
	};

	const onChangeDefault: CheckboxProps['onChange'] = (e) => {
		createForm.setFieldValue('default', e.target.checked ? 1 : 0);
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('CURRENCIES.CREATE_TITLE')}</h3>
			</div>

			<div className="sectionContainer">
				<h5 style={{ marginBottom: 20 }}>{t('CURRENCIES.DETAILS')}</h5>

				<Form name="createForm" form={createForm} onFinish={onSubmit}>
					<Row>
						<Col span={24} md={12}>
							<Form.Item<FieldType> name="name[en]" rules={[{ required: true, message: t('CURRENCIES.VALIDATION.NAME_EN') }]}>
								<Input size="large" placeholder={t('CURRENCIES.PLACEHOLDER.NAME_EN')} />
							</Form.Item>
						</Col>
					</Row>
					<Row>
						<Col span={24} md={12}>
							<Form.Item<FieldType> name="name[ar]" rules={[{ required: true, message: t('CURRENCIES.VALIDATION.NAME_AR') }]}>
								<Input size="large" placeholder={t('CURRENCIES.PLACEHOLDER.NAME_AR')} />
							</Form.Item>
						</Col>
					</Row>
					<Row>
						<Col span={24} md={12}>
							<Form.Item<FieldType>
								name="code"
								rules={[
									{ required: true, message: t('CURRENCIES.VALIDATION.CODE') },
									{ min: 3, max: 3, message: t('CURRENCIES.VALIDATION.CODE_LENGTH') },
								]}>
								<Input size="large" placeholder={t('CURRENCIES.PLACEHOLDER.CODE')} />
							</Form.Item>
						</Col>
					</Row>

					<Row>
						<Col span={24} md={12}>
							<Form.Item<FieldType> name="default" hidden>
								<Input hidden />
							</Form.Item>
							<Checkbox onChange={onChangeDefault}>{t('CURRENCIES.SET_AS_DEFAULT')}</Checkbox>
						</Col>
					</Row>
				</Form>

				<Form.Item>
					<Button
						size="middle"
						type="primary"
						htmlType="submit"
						form="createForm"
						loading={isSubmitting}
						style={{ marginTop: 20, display: 'flex' }}>
						{t('COMMON.ADD')}
					</Button>
				</Form.Item>
			</div>
		</>
	);
}

export default CreateCurrencyPage;
